import { useMemo, useState } from "react";
import { ChoiceField, NumField } from "@/components/tool/CalcFields";
import { numField } from "@/lib/health/body-profile";
import { bodyFatNavy, type Sex } from "@/lib/health/fitness";
import {
  BodyProfileHint,
  useBodyProfileSync,
} from "@/lib/health/use-body-profile";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

export function BodyFatCalc() {
  const locale = getLocale();
  const [sex, setSex] = useState<Sex>("male");
  const [height, setHeight] = useState("180");
  const [neck, setNeck] = useState("38");
  const [waist, setWaist] = useState("85");
  const [hip, setHip] = useState("95");

  const profileSync = useBodyProfileSync({
    apply: (p) => {
      if (p.sex) setSex(p.sex);
      if (p.heightCm) setHeight(String(p.heightCm));
      if (p.neckCm) setNeck(String(p.neckCm));
      if (p.waistCm) setWaist(String(p.waistCm));
      if (p.hipCm) setHip(String(p.hipCm));
    },
    collect: () => ({
      sex,
      heightCm: numField(height),
      neckCm: numField(neck),
      waistCm: numField(waist),
      hipCm: numField(hip),
    }),
    deps: [sex, height, neck, waist, hip],
  });

  const bf = useMemo(() => {
    const h = Number(height);
    const n = Number(neck);
    const w = Number(waist);
    const hp = Number(hip);
    if (![h, n, w].every(Number.isFinite)) return null;
    return bodyFatNavy(sex, h, n, w, sex === "female" ? hp : 0);
  }, [sex, height, neck, waist, hip]);

  return (
    <div className="sci-card rounded-2xl space-y-4 p-6 sm:p-8">
      <ChoiceField
        label={m.h_sex()}
        value={sex}
        onChange={setSex}
        options={[
          { value: "male", label: m.h_male() },
          { value: "female", label: m.h_female() },
        ]}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <NumField
          id="height"
          label={m.h_height_cm()}
          value={height}
          onChange={setHeight}
          suffix="cm"
        />
        <NumField
          id="neck"
          label={m.bf_neck()}
          value={neck}
          onChange={setNeck}
          suffix="cm"
        />
        <NumField
          id="waist"
          label={m.bf_waist()}
          value={waist}
          onChange={setWaist}
          suffix="cm"
        />
        {sex === "female" && (
          <NumField
            id="hip"
            label={m.bf_hip()}
            value={hip}
            onChange={setHip}
            suffix="cm"
          />
        )}
      </div>
      {bf != null ? (
        <div className="sci-result-zone flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
          <span className="text-sm font-medium text-muted-foreground">
            {m.bf_label()}
          </span>
          <span
            data-testid="bf-result"
            className="sci-result font-display text-3xl font-bold sm:text-4xl"
          >
            {bf.toLocaleString(locale, { maximumFractionDigits: 1 })}%
          </span>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{m.bf_hint()}</p>
      )}
      <BodyProfileHint {...profileSync} />
    </div>
  );
}
